import { useState } from "react"

interface UseLocalStorageOptions<T> {
  defaultValue: T
  serialize?: (value: T) => string
  deserialize?: (value: string) => T
}

export function useLocalStorage<T>(
  key: string,
  options: UseLocalStorageOptions<T>
) {
  const {
    defaultValue,
    serialize = JSON.stringify,
    deserialize = JSON.parse,
  } = options

  // Get initial value from localStorage or use default
  const [value, setValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key)
      return item ? deserialize(item) : defaultValue
    } catch (error) {
      // Error reading localStorage - using default value
      return defaultValue
    }
  })

  // Update localStorage when value changes
  const setStoredValue = (newValue: T | ((prevValue: T) => T)) => {
    try {
      const valueToStore =
        newValue instanceof Function ? newValue(value) : newValue
      setValue(valueToStore)
      window.localStorage.setItem(key, serialize(valueToStore))
    } catch (error) {
      // Error setting localStorage value
    }
  }

  // Remove from localStorage
  const removeValue = () => {
    try {
      window.localStorage.removeItem(key)
      setValue(defaultValue)
    } catch (error) {
      // Error removing localStorage value
    }
  }

  return [value, setStoredValue, removeValue] as const
}

// Hook específico para las preferencias de la aplicación
export function useVisitsPreferences() {
  const [displayMode, setDisplayMode] = useLocalStorage("visits_display_mode", {
    defaultValue: "map" as "map" | "list" | "card",
  })

  const [showFilter, setShowFilter] = useLocalStorage("visits_show_filter", {
    defaultValue: false,
  })

  const [filter, setFilter] = useLocalStorage("visits_filter", {
    defaultValue: {
      from: null,
      until: null,
      sector: "",
      name: "",
      stageId: null,
      stageIds: [] as string[],
      categoryId: null,
      assignedTo: null,
      placeStatusId: null,
      withVehicleVisits: null,
      revisitPending: false,
    },
  })

  const [showFinished, setShowFinished] = useLocalStorage(
    "visits_show_finished",
    {
      defaultValue: true,
    }
  )
  const [withVehicleVisits, setWithVehicleVisits] = useLocalStorage(
    "visits_with_vehicle_visits",
    {
      defaultValue: null,
    }
  )
  const [showTrucks, setShowTrucks] = useLocalStorage("visits_show_trucks", {
    defaultValue: false,
  })

  const [mapProvider, setMapProvider] = useLocalStorage("visits_map_provider", {
    defaultValue: "google" as "google" | "osm" | "mapbox",
  })

  const [mapboxToken, setMapboxToken] = useLocalStorage("visits_mapbox_token", {
    defaultValue: "",
  })

  return {
    displayMode,
    setDisplayMode,
    showFilter,
    setShowFilter,
    filter,
    setFilter,
    showFinished,
    setShowFinished,
    showTrucks,
    setShowTrucks,
    withVehicleVisits,
    setWithVehicleVisits,
    mapProvider,
    setMapProvider,
    mapboxToken,
    setMapboxToken,
  }
}
